import Image from "next/image";
import Link from "next/link";
import parse from "html-react-parser";
import { toggleEmailOptIn as trackToggleEmailOptIn } from "@/lib/segment";

import EmailOptIn from "@/components/EmailOptIn/EmailOptIn";

import { PartialPartner } from "@/common/partner/types";
import useUserEnrollments from "../../CourseContext/hooks/useUserEnrollments";
import CourseRating from "../../CourseRating";
import { ProcessedCourse } from "@/common/course/types";
import CourseRunSelector from "../../ContentRefresh/CourseRunSelector";
import { hideEmailOptInForCourse } from "../../CourseRunSelector/constants";
import isCourseArchived from "@/lib/course/utils/isCourseArchived";
import useEmailOptIn from "../../CourseContext/hooks/useEmailOptIn";
import { partnerImageDimensions } from "@/constants/hero/imageDimensions";
import ExpandableTextByLines from "../../Expand/ExpandableTextByLines";
import { partnerKeyBreakpoint } from "../../styles/constants";
import useWebview from "@/webview/useWebview";
import { MIN_ENROLLMENT_COUNT_FOR_DISPLAY } from "../../CourseRunSelector/constants";
import { getPartnersDisplayNames } from "@/lib/partner/utils/getPartnerDisplayName";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import DynamicBadges from "@/components/ProgramDetailLayout/templates/components/TaggedHeader/DynamicBadges";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import CurrencyInlinePill from "@/components/CurrencySelector/CurrencyInlinePill";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import type { DetailPricingData } from "@/lib/checkout-geo-pricing";
import HeroAccentCallout from "@/components/GeoPricing/HeroAccentCallout";
import { getProgramPricingAmounts } from "@/lib/currency/getProgramPricingAmounts";
import useTranslations from "@/i18n/useTranslations";

const VARIANT_STYLES = {
  light: {
    bg: "bg-putty-100",
    text: "text-[#002626]",
    badge: "bg-putty-400 border-putty-700",
    badgeText: "text-black",
    border: "border-putty-400",
  },
  dark: {
    bg: "bg-primary-500",
    text: "text-white",
    badge: "bg-[#BEF4E5] border-[#004f4f]",
    badgeText: "text-primary-500",
    border: "border-[#003838]",
  },
};
export interface ICoursePageHeroProps {
  course: ProcessedCourse;
  variant?: "light" | "dark";
  pricingData?: DetailPricingData | null;
}

export default function NewCourseHeader({
  course,
  variant = "dark",
  pricingData,
}: ICoursePageHeroProps) {
  const t = useTranslations("Course");
  const { emailOptIn, setEmailOptIn } = useEmailOptIn();
  const { isUserEnrolled } = useUserEnrollments();
  const { activeCourseRun } = course;
  const partners = course.partners.length ? course.partners : [];
  const isArchived = activeCourseRun ? isCourseArchived(activeCourseRun) : true;
  const description = course.productShortDescription;
  const id = course.productUuid;
  const productName = course.productName;
  const courseReview = course.ratings;
  const titlePartner = getPartnersDisplayNames(course.partners);
  const title = `${titlePartner}: ${course.productName}`.replace(/\.$/, "");
  const { getUrl } = useWebview();

  const enrollmentCount = course.enrollmentCount;
  const showEnrollmentCount =
    !isArchived &&
    activeCourseRun?.isEnrollable &&
    enrollmentCount &&
    enrollmentCount >= MIN_ENROLLMENT_COUNT_FOR_DISPLAY;
  const toggleEmailOptIn = () => {
    const optIn = !emailOptIn;
    setEmailOptIn(optIn);
    if (activeCourseRun?.key) {
      trackToggleEmailOptIn({
        courseRunKey: activeCourseRun?.key,
        optIn: optIn,
      });
    }
  };
  const courseDescription = parse(description);
  const {
    insertBadges,
    showDynamicBadges,
    showHeaderFactoidBar,
    straightToCheckout,
    currencySelector,
  } = useSingleCourseTrafficConfig({ course });
  const amounts = getProgramPricingAmounts(pricingData);
  const coursePrice =
    amounts?.price ??
    (activeCourseRun ? getCoursePrice(activeCourseRun) : null);
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.dark;
  const leadWithPaidData = useLeadWithPaid(course);

  return (
    <div
      className={`bg-white rounded-xl py-6 px-4 sm:p-10 md:flex md:items-start md:justify-between}`}
    >
      <div className="flex flex-col gap-5 w-full">
        <div className="flex border-putty-400 border-b pb-4 w-full gap-4">
          <div className="flex flex-col">
            <div className="flex justify-center sm:justify-start">
              {partners.map((partner: PartialPartner) => {
                return partner.showOnSchoolsAndPartnersPage ? (
                  <Link
                    className="d-inline-block"
                    key={partner.key}
                    href={getUrl(`/school/${partner.slug}`) || ""}
                  >
                    <Image
                      src={partner.logoUrl || ""}
                      width={partnerImageDimensions.width}
                      height={partnerImageDimensions.height}
                      alt={title || ""}
                      title={title || ""}
                      key={partner.key}
                      className="max-w-[165px] md:max-w-full"
                    />
                  </Link>
                ) : (
                  <Image
                    src={partner.logoUrl || ""}
                    width={partnerImageDimensions.width}
                    height={partnerImageDimensions.height}
                    alt={title || ""}
                    title={title || ""}
                    key={partner.key}
                    className="max-w-[165px] md:max-w-full"
                  />
                );
              })}
            </div>
            <div className="flex flex-col gap-5">
              <h1
                className={`m-0 p-0 tracking-tight ${titlePartner.length > partnerKeyBreakpoint ? "max-md:text-xl max-lg:text-2xl" : ""}`}
                style={{ fontWeight: 900 }}
              >
                {title}
              </h1>
              <ExpandableTextByLines
                text={courseDescription}
                className="md:[&_p]:text-lg [&_p]:m-0 w-full"
                maxLines={2}
              />
              <HeroAccentCallout
                pricingData={pricingData}
                variant="light"
                className="mt-4"
                location="course-detail-page"
              />
              {currencySelector?.placement === "inline-dropdown" &&
                coursePrice && (
                  <FeatureFlagComponentResolverClient
                    flagKey="NEXT_PUBLIC_FEATURE_MULTI_CURRENCY_ACCEPTANCE"
                    variants={{
                      legacy: null,
                      test: (
                        <CurrencyInlinePill
                          price={coursePrice}
                          originalPrice={amounts?.originalPrice}
                          label={currencySelector?.label}
                          mobileLabel={currencySelector?.mobileLabel}
                          location="single-course-detail-page"
                          tooltip={currencySelector?.tooltip}
                          countryAutoDetection={
                            currencySelector?.countryAutoDetection
                          }
                          variant={variant === "dark" ? "inverse" : "default"}
                          className="mt-2"
                        />
                      ),
                    }}
                    fallback="legacy"
                  />
                )}
              {showEnrollmentCount && (
                <div className="flex items-center text-base">
                  <span className="font-bold m-0">
                    {enrollmentCount.toLocaleString()}
                  </span>
                  <span className="m-0">&nbsp;{t("learnersEnrolled")}</span>
                </div>
              )}
              {insertBadges && (
                <DynamicBadges
                  courseData={course}
                  styles={{
                    badge: styles.badge,
                    badgeText: styles.badgeText,
                    margin: "!m-0",
                  }}
                  insertBadges={insertBadges}
                />
              )}
              {!showHeaderFactoidBar && showDynamicBadges && (
                <DynamicBadges
                  courseData={course}
                  styles={{
                    badge: styles.badge,
                    badgeText: styles.badgeText,
                    margin: "!m-0",
                  }}
                  leadWithPaidData={leadWithPaidData}
                />
              )}
              <CourseRunSelector
                course={course}
                name="hero-cta"
                location="hero"
                variant="inlineHeader"
                straightToCheckout={straightToCheckout}
                pricingData={pricingData}
              />
            </div>
          </div>
        </div>

        <div className="hidden md:flex md:flex-col">
          {!hideEmailOptInForCourse.includes(id) &&
            !isUserEnrolled &&
            !isArchived &&
            activeCourseRun?.isEnrollable && (
              <EmailOptIn
                optIn={emailOptIn}
                partners={partners}
                title={productName}
                toggleEmailOptIn={toggleEmailOptIn}
                variant="secondary"
              />
            )}
        </div>
      </div>
    </div>
  );
}
